import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

interface SurveyQuestion {
  id: string;
  type: "rating" | "nps" | "yesno" | "multipleChoice" | "text";
  text: string;
  options?: string[] | null;
  required?: boolean;
}

interface Survey {
  id: string;
  title: string;
  description: string;
  questions: SurveyQuestion[];
  settings: {
    allowAnonymous: boolean;
    requireEmail: boolean;
  };
}

export const SurveyPage: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const response = await fetch(
          `https://us-central1-businessfactory-app.cloudfunctions.net/getSurvey?id=${surveyId}`
        );

        if (!response.ok) {
          throw new Error("Survey not found");
        }

        const data = await response.json();
        setSurvey(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load survey");
      } finally {
        setLoading(false);
      }
    };

    if (surveyId) {
      fetchSurvey();
    }
  }, [surveyId]);

  const handleAnswer = (questionId: string, value: any) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!survey || !surveyId) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(
        "https://us-central1-businessfactory-app.cloudfunctions.net/submitSurveyResponse",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            surveyId,
            answers,
            respondentEmail: email || null,
            source: "direct",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit response");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = survey
    ? Math.round(((currentQuestion + 1) / survey.questions.length) * 100)
    : 0;

  const canSubmit = () => {
    if (!survey) return false;

    // Check required fields
    for (const question of survey.questions) {
      if (question.required && answers[question.id] === undefined) {
        return false;
      }
    }

    if (survey.settings.requireEmail && !email) {
      return false;
    }

    return true;
  };

  if (loading) {
    return (
      <div className="survey-page loading">
        <div className="survey-container">
          <div className="loading-spinner"></div>
          <p>Loading survey...</p>
        </div>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="survey-page error">
        <div className="survey-container">
          <div className="error-icon">!</div>
          <h1>Survey Not Found</h1>
          <p>{error || "This survey doesn't exist or is no longer available."}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="survey-page success">
        <div className="survey-container">
          <div className="success-icon">✓</div>
          <h1>Thank You!</h1>
          <p>Your response has been recorded successfully.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-page">
      <style>{`
        .survey-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .survey-page.loading, .survey-page.error, .survey-page.success {
          text-align: center;
          color: white;
        }

        .survey-container {
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          max-width: 600px;
          width: 100%;
          overflow: hidden;
        }

        .survey-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 32px;
          text-align: center;
        }

        .survey-header h1 {
          margin: 0 0 8px 0;
          font-size: 1.75rem;
          font-weight: 700;
        }

        .survey-header p {
          margin: 0;
          opacity: 0.9;
        }

        .progress-bar {
          height: 4px;
          background: rgba(255, 255, 255, 0.3);
          margin-top: 20px;
          border-radius: 2px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: white;
          transition: width 0.3s ease;
        }

        .survey-content {
          padding: 32px;
        }

        .question {
          margin-bottom: 32px;
        }

        .question:last-child {
          margin-bottom: 0;
        }

        .question-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: #667eea;
          color: white;
          border-radius: 50%;
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .question-text {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 16px;
        }

        .rating-options {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .rating-option {
          flex: 1;
          min-width: 48px;
          padding: 12px 8px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          text-align: center;
          transition: all 0.2s;
        }

        .rating-option:hover {
          border-color: #667eea;
        }

        .rating-option.selected {
          background: #667eea;
          border-color: #667eea;
          color: white;
        }

        .rating-option .value {
          font-size: 1.25rem;
          font-weight: 700;
        }

        .rating-option .label {
          font-size: 0.75rem;
          opacity: 0.7;
          margin-top: 4px;
        }

        .nps-options {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }

        .nps-option {
          flex: 1;
          min-width: 32px;
          padding: 8px 4px;
          border: 2px solid #e5e7eb;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          text-align: center;
          font-weight: 600;
          transition: all 0.2s;
        }

        .nps-option:hover {
          border-color: #667eea;
        }

        .nps-option.selected {
          background: #667eea;
          border-color: #667eea;
          color: white;
        }

        .nps-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
          font-size: 0.75rem;
          color: #6b7280;
        }

        .yesno-options {
          display: flex;
          gap: 16px;
        }

        .yesno-option {
          flex: 1;
          padding: 16px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          background: white;
          cursor: pointer;
          text-align: center;
          font-size: 1.125rem;
          font-weight: 600;
          transition: all 0.2s;
        }

        .yesno-option:hover {
          border-color: #667eea;
        }

        .yesno-option.selected.yes {
          background: #10b981;
          border-color: #10b981;
          color: white;
        }

        .yesno-option.selected.no {
          background: #ef4444;
          border-color: #ef4444;
          color: white;
        }

        .choice-options {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .choice-option {
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .choice-option:hover {
          border-color: #667eea;
        }

        .choice-option.selected {
          background: #667eea;
          border-color: #667eea;
          color: white;
        }

        .text-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.2s;
          resize: vertical;
          min-height: 100px;
        }

        .text-input:focus {
          outline: none;
          border-color: #667eea;
        }

        .email-section {
          background: #f9fafb;
          padding: 20px;
          border-radius: 12px;
          margin-bottom: 24px;
        }

        .email-section label {
          display: block;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .email-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 1rem;
        }

        .email-input:focus {
          outline: none;
          border-color: #667eea;
        }

        .submit-btn {
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1.125rem;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .error-message {
          background: #fef2f2;
          color: #dc2626;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }

        .success-icon, .error-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          margin: 0 auto 20px;
        }

        .success-icon {
          background: #10b981;
        }

        .error-icon {
          background: #ef4444;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 480px) {
          .survey-header {
            padding: 24px;
          }

          .survey-header h1 {
            font-size: 1.5rem;
          }

          .survey-content {
            padding: 24px;
          }

          .rating-option {
            min-width: 40px;
            padding: 10px 6px;
          }

          .nps-option {
            min-width: 28px;
            padding: 6px 2px;
            font-size: 0.875rem;
          }
        }
      `}</style>

      <div className="survey-container">
        <div className="survey-header">
          <h1>{survey.title}</h1>
          {survey.description && <p>{survey.description}</p>}
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        <div className="survey-content">
          {error && <div className="error-message">{error}</div>}

          {survey.questions.map((question, index) => (
            <div key={question.id} className="question">
              <div className="question-number">{index + 1}</div>
              <div className="question-text">{question.text}</div>

              {question.type === "rating" && (
                <div className="rating-options">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      className={`rating-option ${
                        answers[question.id] === value ? "selected" : ""
                      }`}
                      onClick={() => handleAnswer(question.id, value)}
                    >
                      <div className="value">{value}</div>
                      <div className="label">
                        {value === 1
                          ? "Disagree"
                          : value === 5
                          ? "Agree"
                          : ""}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {question.type === "nps" && (
                <>
                  <div className="nps-options">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                      <button
                        key={value}
                        className={`nps-option ${
                          answers[question.id] === value ? "selected" : ""
                        }`}
                        onClick={() => handleAnswer(question.id, value)}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                  <div className="nps-labels">
                    <span>Not likely</span>
                    <span>Very likely</span>
                  </div>
                </>
              )}

              {question.type === "yesno" && (
                <div className="yesno-options">
                  <button
                    className={`yesno-option ${
                      answers[question.id] === "yes" ? "selected yes" : ""
                    }`}
                    onClick={() => handleAnswer(question.id, "yes")}
                  >
                    Yes
                  </button>
                  <button
                    className={`yesno-option ${
                      answers[question.id] === "no" ? "selected no" : ""
                    }`}
                    onClick={() => handleAnswer(question.id, "no")}
                  >
                    No
                  </button>
                </div>
              )}

              {question.type === "multipleChoice" && question.options && (
                <div className="choice-options">
                  {question.options.map((option) => (
                    <button
                      key={option}
                      className={`choice-option ${
                        answers[question.id] === option ? "selected" : ""
                      }`}
                      onClick={() => handleAnswer(question.id, option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}

              {question.type === "text" && (
                <textarea
                  className="text-input"
                  placeholder="Type your answer here..."
                  value={answers[question.id] || ""}
                  onChange={(e) => handleAnswer(question.id, e.target.value)}
                />
              )}
            </div>
          ))}

          {(survey.settings.requireEmail || !survey.settings.allowAnonymous) && (
            <div className="email-section">
              <label>
                Your Email {survey.settings.requireEmail && "*"}
              </label>
              <input
                type="email"
                className="email-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          )}

          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={submitting || !canSubmit()}
          >
            {submitting ? "Submitting..." : "Submit Response"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyPage;
